import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
	HttpInterceptor,
	HttpHandler,
	HttpRequest,
	HttpEvent,
	HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, shareReplay, take } from 'rxjs/operators';

/**
 * ipapi.co was being called once per component that needed geo data (6 call
 * sites => ~4 requests per page load), including during server-side rendering,
 * where it added a third-party round trip to TTFB and drew Cloudflare bot
 * challenges from the server's IP.
 *
 * This interceptor leaves the call sites untouched and instead:
 *   - skips the request entirely on the server (returns an empty body), and
 *   - performs at most one real request per browser session, sharing the
 *     result with every later caller.
 *
 * It never errors: geo data is decorative here, so failures degrade to {}.
 */
@Injectable()
export class IpapiInterceptor implements HttpInterceptor {
	private cached$?: Observable<HttpEvent<any>>;

	constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (req.url.indexOf('ipapi.co') === -1) {
			return next.handle(req);
		}

		const empty = () => of(new HttpResponse({ status: 200, body: {} }) as HttpEvent<any>);

		// Never call a third-party geo API while server-rendering.
		if (!isPlatformBrowser(this.platformId)) {
			return empty();
		}

		if (!this.cached$) {
			this.cached$ = next.handle(req).pipe(
				filter((event) => event instanceof HttpResponse),
				catchError(() => empty()),
				shareReplay({ bufferSize: 1, refCount: false }),
			);
		}

		return this.cached$.pipe(take(1));
	}
}
